import { useMemo } from "react";
import { usePmJobsByDevice } from "@/hooks/maintenance/usePmJobsByDevice";
import { DisplayMaintenance, MaintenanceDisplayData } from "@/models/MaintenanceDisplayData";
import combineMaintenanceData from "@/pages/maintenance/utils/combineMaintenanceData";

/**
 * Rows for the maintenance detail modal. When {@link DisplayMaintenance.deviceId} is set
 * (PM-warning open path), rows are re-sourced from the live `usePmJobsByDevice` query so a
 * status change inside the modal refreshes the table once the mutation invalidates the cache —
 * the `workOrderJobs` snapshot is only a non-flickering fallback while the query loads. Without
 * a deviceId (MaintenanceCell path) the query stays disabled and the snapshot is used verbatim.
 */
export function useMaintenanceModalRows(
  modalData: DisplayMaintenance | null,
): MaintenanceDisplayData[] {
  const deviceId = modalData?.deviceId ?? "";
  const { data: liveJobs } = usePmJobsByDevice(deviceId);

  return useMemo(() => {
    if (!modalData) return [];
    const snapshot = modalData.workOrderJobs ?? [];
    const jobs = deviceId ? (liveJobs ?? snapshot) : snapshot;
    return combineMaintenanceData(modalData.workOrders ?? [], jobs);
  }, [modalData, deviceId, liveJobs]);
}
